/**
 * VisualizerEngine.
 *
 * Owns the WebGPU device, the feedback ring, the three Phase 1 passes, the
 * audio pipeline, the beat injector, the preset registry, and the modulation
 * evaluator. Drives one frame at a time via `step(nowMs)`.
 *
 * Phase 1 invariant: pass order is fixed (warp -> seed -> present), pass
 * pipelines are built once, and per-frame work is just uniform writes +
 * encode + submit. No per-frame graph construction, no DAG.
 */

import { acquireDevice, type GpuContext } from '../../renderer/webgpu/device';
import {
  attachSurface,
  setInternalRenderScale,
  watchResize,
  type Surface,
} from '../../renderer/webgpu/swapchain';
import {
  clearRing,
  createFramebufferRing,
  destroyRing,
  rotateRing,
  type FramebufferRing,
} from '../../renderer/webgpu/framebufferRing';
import { FrameUniforms } from '../../renderer/webgpu/uniforms';
import { WaveformSeedPass, type SeedParams } from '../../renderer/webgpu/passes/seedPass';
import {
  FeedbackWarpPass,
  type WarpParams,
} from '../../renderer/webgpu/passes/feedbackWarpPass';
import {
  PresentMode,
  PresentPass,
  type PresentParams,
} from '../../renderer/webgpu/passes/presentPass';
import {
  createAudioPipeline,
  type AudioFrame,
  type AudioPipeline,
} from '../../audio/analysis/analyser';
import {
  createBeatInjector,
  type BeatInjector,
} from '../../audio/analysis/beatTest';
import { ModulationEvaluator } from '../modulation/evaluator';
import type { ParamBundle } from '../modulation/targets';
import { PresetRegistry } from '../presets/presetRegistry';
import type { VisualizerPreset } from '../presets/presetTypes';
import { WarpFieldGenPass } from '../../renderer/webgpu/passes/warpFieldGenPass';
import {
  DEFAULT_FIELD_RESOLUTION,
  WarpFieldSystem,
  type WarpFieldState,
} from '../warpField/WarpFieldSystem';
import { BloomPass, type BloomParams } from '../../renderer/webgpu/passes/bloomPass';
import {
  CompositePass,
  MirrorMode,
  type CompositeParams,
} from '../../renderer/webgpu/passes/compositePass';
import {
  AutoCycler,
  DEFAULT_AUTO_CYCLER_CONFIG,
  type AutoCyclerStatus,
} from '../auto/AutoCycler';
import { AUTO_PRESET_ID } from '../presets/geiss/auto';

export type EngineCreateOptions = {
  internalRenderScale?: number;
};

export type FrameInfo = {
  fps: number;
  frameMs: number;
  audio: AudioFrame;
  presetId: string;
  presetName: string;
  transitionT: number | null;
  internalScale: number;
  internalSize: [number, number];
  presentMode: PresentMode;
  beatInjectorMs: number | null;
  /**
   * Source of the most recent beat tick:
   *   - 'real'  : detector fired this frame
   *   - 'synth' : the manual / auto injector fired this frame
   *   - 'none'  : no tick this frame
   * Useful for HUD labeling so the user can tell organic from synthetic beats.
   */
  beatSource: 'real' | 'synth' | 'none';
  /**
   * Auto-cycler status. `enabled: false` when the user has selected a
   * specific preset; `enabled: true` when running in auto-DJ mode.
   */
  auto: AutoCyclerStatus;
  /** Live state of the WarpFieldSystem, or null when no field is active. */
  warpField: WarpFieldState | null;
};

export class VisualizerEngine {
  readonly device: GPUDevice;
  readonly surface: Surface;
  readonly audio: AudioPipeline;
  readonly registry = new PresetRegistry();
  readonly beatInjector: BeatInjector = createBeatInjector();
  /**
   * Auto-DJ. Lazily configured in `registerPresets` once we know the
   * candidate ids. Until then, `loadPreset('auto')` is a no-op.
   */
  private autoCycler: AutoCycler | null = null;
  private lastAutoStatus: AutoCyclerStatus = {
    enabled: false,
    currentChildId: null,
    msUntilNextTarget: null,
    waitingForBeat: false,
    forceImminent: false,
  };

  private readonly gpu: GpuContext;
  private ring: FramebufferRing;
  private readonly frameUniforms: FrameUniforms;
  private readonly seedPass: WaveformSeedPass;
  private readonly warpPass: FeedbackWarpPass;
  private readonly presentPass: PresentPass;

  // ---- WarpFieldSystem (Phase 2) ------------------------------------------
  private readonly fieldGenPass: WarpFieldGenPass;
  private warpFieldSystem: WarpFieldSystem | null = null;
  private warpFieldOwner: string | null = null; // preset id that owns the system
  /** 2x2 placeholder bound when no warp field is active. */
  private readonly placeholderField: GPUTexture;
  private readonly placeholderFieldView: GPUTextureView;
  private lastWarpFieldState: WarpFieldState | null = null;
  private lastBeatSource: 'real' | 'synth' | 'none' = 'none';

  // ---- Bloom (Phase 2) ----------------------------------------------------
  private readonly bloomPass: BloomPass;
  /** 2x2 placeholder bound when bloom is disabled. */
  private readonly placeholderBloom: GPUTexture;
  private readonly placeholderBloomView: GPUTextureView;
  private bloomParams: BloomParams = {
    threshold: 0.6,
    knee: 0.18,
    spread: 1.0,
  };

  // ---- Composite (Phase 2) ------------------------------------------------
  private readonly compositePass: CompositePass;
  private compositeParams: CompositeParams = {
    mode: MirrorMode.None,
    segments: 6,
    contrast: 1.0,
    brightness: 0,
  };

  private readonly evaluatorA = new ModulationEvaluator();
  private readonly evaluatorB = new ModulationEvaluator();
  /** Working bundle for the destination preset. */
  private readonly bundleA: ParamBundle;
  /** Working bundle for the source preset (used during transitions). */
  private readonly bundleB: ParamBundle;
  /** Final (lerped) bundle uploaded to the GPU each frame. */
  private readonly bundleOut: ParamBundle;

  private presentParams: PresentParams = {
    mode: PresentMode.Normal,
    tonemapEnabled: true,
    gammaEnabled: true,
    exposure: 1.15,
    vignette: 0.18,
    bloomEnabled: false,
    bloomIntensity: 0,
    grain: 0,
  };
  /** Tracks which preset id last wrote into presentParams so we know to re-apply. */
  private presentPresetId: string | null = null;
  private currentPresetIdForEvaluator: string | null = null;
  private currentTransitionFromIdForEvaluator: string | null = null;

  // Smoothed HUD-facing readouts.
  private smoothedFps = 60;
  private smoothedFrameMs = 16.7;
  private lastClockMs = 0;
  private frameIndex = 0;

  static async create(
    canvas: HTMLCanvasElement,
    opts: EngineCreateOptions = {},
  ): Promise<VisualizerEngine> {
    const gpu = await acquireDevice();
    return new VisualizerEngine(gpu, canvas, opts);
  }

  private constructor(
    gpu: GpuContext,
    canvas: HTMLCanvasElement,
    opts: EngineCreateOptions,
  ) {
    this.gpu = gpu;
    this.device = gpu.device;

    this.surface = attachSurface(this.device, canvas, {
      ...(opts.internalRenderScale !== undefined
        ? { internalRenderScale: opts.internalRenderScale }
        : {}),
    });

    this.ring = createFramebufferRing(
      this.device,
      gpu.capabilities.feedbackFormat,
      this.surface.renderWidth,
      this.surface.renderHeight,
    );
    clearRing(this.device, this.ring);

    this.frameUniforms = new FrameUniforms(this.device);
    this.seedPass = new WaveformSeedPass(
      this.device,
      gpu.capabilities.feedbackFormat,
      this.frameUniforms.buffer,
    );
    this.warpPass = new FeedbackWarpPass(
      this.device,
      gpu.capabilities.feedbackFormat,
      this.frameUniforms.buffer,
    );
    this.presentPass = new PresentPass(
      this.device,
      this.surface.format,
      this.frameUniforms.buffer,
    );
    this.presentPass.updateParams(this.presentParams);

    // Warp-field generator pipeline + a tiny placeholder texture that gets
    // bound when the active preset doesn't use a warp field.
    this.fieldGenPass = new WarpFieldGenPass(this.device, 'rgba16float');
    this.placeholderField = this.device.createTexture({
      label: 'warp-field-placeholder',
      size: { width: 2, height: 2 },
      format: 'rgba16float',
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });
    this.placeholderFieldView = this.placeholderField.createView();
    // Bloom pipeline + a 2x2 placeholder bound when bloom is disabled.
    this.bloomPass = new BloomPass(
      this.device,
      this.surface.renderWidth,
      this.surface.renderHeight,
    );
    // Composite pipeline (mirror modes, contrast). Always runs, even when
    // mode = None: the cost is one fragment-shader pass that essentially
    // does a single bilinear sample, and it removes a bunch of branching
    // in the engine's encoding path.
    this.compositePass = new CompositePass(
      this.device,
      this.surface.renderWidth,
      this.surface.renderHeight,
    );
    this.placeholderBloom = this.device.createTexture({
      label: 'bloom-placeholder',
      size: { width: 2, height: 2 },
      format: 'rgba16float',
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });
    this.placeholderBloomView = this.placeholderBloom.createView();
    // Clear placeholders to zero so any accidental sample reads (0,0,0,1).
    {
      const enc = this.device.createCommandEncoder({ label: 'placeholder-clear' });
      for (const view of [this.placeholderFieldView, this.placeholderBloomView]) {
        const pass = enc.beginRenderPass({
          colorAttachments: [
            {
              view,
              loadOp: 'clear',
              clearValue: { r: 0, g: 0, b: 0, a: 1 },
              storeOp: 'store',
            },
          ],
        });
        pass.end();
      }
      this.device.queue.submit([enc.finish()]);
    }

    this.bundleA = makeEmptyBundle();
    this.bundleB = makeEmptyBundle();
    this.bundleOut = makeEmptyBundle();

    this.audio = createAudioPipeline();

    watchResize(this.surface, () => this.handleResize());
  }

  // ---- Warp-field lifecycle (preset-driven) -------------------------------

  private syncWarpFieldForActive(currentPreset: VisualizerPreset): void {
    const wantsField = currentPreset.warpField !== undefined;
    const sameOwner = this.warpFieldOwner === currentPreset.id;
    if (!wantsField) {
      if (this.warpFieldSystem) {
        this.warpFieldSystem.destroy();
        this.warpFieldSystem = null;
        this.warpFieldOwner = null;
        this.lastWarpFieldState = null;
      }
      return;
    }
    if (!this.warpFieldSystem) {
      this.warpFieldSystem = new WarpFieldSystem(
        this.device,
        this.fieldGenPass,
        currentPreset.warpField!,
        DEFAULT_FIELD_RESOLUTION,
      );
      this.warpFieldOwner = currentPreset.id;
      return;
    }
    if (!sameOwner) {
      // Same engine, new preset owner. Reconfigure (may swap generator).
      this.warpFieldSystem.setConfig(currentPreset.warpField!);
      this.warpFieldOwner = currentPreset.id;
    }
  }

  // ---- Audio sources (the engine doesn't own the choice; main wires this) -

  /** Connect an external AudioNode (from a capture module) into the analyser. */
  attachAudioSource(node: AudioNode): void {
    node.connect(this.audio.input);
  }

  detachAudioSource(node: AudioNode): void {
    try {
      node.disconnect(this.audio.input);
    } catch {
      // already disconnected
    }
  }

  async resumeAudio(): Promise<void> {
    if (this.audio.ctx.state === 'suspended') await this.audio.ctx.resume();
  }

  // ---- Presets ------------------------------------------------------------

  registerPresets(presets: VisualizerPreset[]): void {
    this.registry.registerMany(presets);
  }

  /**
   * Configure the auto-DJ. Must be called after `registerPresets`. `children`
   * is the set of preset ids the cycler is allowed to choose between (it
   * should NOT include the auto preset itself, and typically excludes the
   * phase 0 baseline). Pass at least 2 ids; otherwise the cycler stays off.
   */
  configureAutoCycler(children: readonly string[]): void {
    if (children.length < 2) return;
    this.autoCycler = new AutoCycler({
      ...DEFAULT_AUTO_CYCLER_CONFIG,
      candidates: children,
    });
  }

  /**
   * Load a preset by id. Special-cases `auto`:
   *   - If the cycler is configured, enables it and immediately picks a
   *     starting child (so the registry never actually renders `auto`).
   *   - Any non-auto id disables the cycler so the user's choice sticks.
   */
  loadPreset(id: string, nowMs: number = performance.now()): void {
    if (id === AUTO_PRESET_ID) {
      this.enableAuto(nowMs);
      return;
    }
    if (this.autoCycler?.isEnabled()) this.autoCycler.stop();
    this.registry.loadById(id, nowMs);
  }

  /**
   * In auto mode, all of next/prev/random/space collapse to "pick a new
   * random child *now*". Out of auto mode they preserve the historical
   * meaning (cycle through the registered list).
   */
  nextPreset(nowMs: number = performance.now()): void {
    if (this.autoCycler?.isEnabled()) {
      const child = this.autoCycler.forceSwitch(nowMs);
      this.registry.loadById(child, nowMs);
      return;
    }
    this.registry.next(nowMs);
  }

  prevPreset(nowMs: number = performance.now()): void {
    if (this.autoCycler?.isEnabled()) {
      const child = this.autoCycler.forceSwitch(nowMs);
      this.registry.loadById(child, nowMs);
      return;
    }
    this.registry.prev(nowMs);
  }

  loadPresetAt(index: number, nowMs: number = performance.now()): void {
    const id = this.registry.idAt(index);
    if (id === null) return;
    this.loadPreset(id, nowMs);
  }

  randomPreset(nowMs: number = performance.now()): void {
    if (this.autoCycler?.isEnabled()) {
      const child = this.autoCycler.forceSwitch(nowMs);
      this.registry.loadById(child, nowMs);
      return;
    }
    this.registry.random(nowMs);
  }

  private enableAuto(nowMs: number): void {
    if (!this.autoCycler) {
      // Cycler not configured (caller forgot to call configureAutoCycler).
      // Fall back to the registered placeholder so we don't black-screen,
      // but the rest of the system will keep working normally.
      this.registry.loadById(AUTO_PRESET_ID, nowMs);
      return;
    }
    const child = this.autoCycler.start(nowMs, this.registry.currentId());
    this.registry.loadById(child, nowMs);
  }

  // ---- Present-pass controls ---------------------------------------------

  cyclePresentMode(): void {
    this.presentParams = {
      ...this.presentParams,
      mode: ((this.presentParams.mode + 1) % 4) as PresentMode,
    };
    this.presentPass.updateParams(this.presentParams);
  }

  // ---- Internal render scale ---------------------------------------------

  bumpInternalScale(delta: number): void {
    setInternalRenderScale(this.surface, this.surface.internalRenderScale + delta);
    this.handleResize();
  }

  private handleResize(): void {
    destroyRing(this.ring);
    this.ring = createFramebufferRing(
      this.device,
      this.gpu.capabilities.feedbackFormat,
      this.surface.renderWidth,
      this.surface.renderHeight,
    );
    clearRing(this.device, this.ring);
    this.bloomPass.resize(this.surface.renderWidth, this.surface.renderHeight);
    this.compositePass.resize(this.surface.renderWidth, this.surface.renderHeight);
  }

  // ---- Per-preset present overrides --------------------------------------

  private syncPresentForPreset(preset: VisualizerPreset): void {
    if (this.presentPresetId === preset.id) return;
    this.presentPresetId = preset.id;
    const cfg = preset.present;
    // Per-preset overrides only touch fields the preset opted into, so other
    // engine state (mode, exposure, tonemap toggles) keeps whatever the HUD
    // controls last set.
    if (cfg) {
      if (cfg.bloomEnabled !== undefined) this.presentParams.bloomEnabled = cfg.bloomEnabled;
      if (cfg.bloomIntensity !== undefined) this.presentParams.bloomIntensity = cfg.bloomIntensity;
      if (cfg.vignette !== undefined) this.presentParams.vignette = cfg.vignette;
      if (cfg.grain !== undefined) this.presentParams.grain = cfg.grain;
      if (cfg.bloomThreshold !== undefined || cfg.bloomKnee !== undefined || cfg.bloomSpread !== undefined) {
        this.bloomParams = {
          threshold: cfg.bloomThreshold ?? this.bloomParams.threshold,
          knee: cfg.bloomKnee ?? this.bloomParams.knee,
          spread: cfg.bloomSpread ?? this.bloomParams.spread,
        };
      }
    } else {
      // Preset opts OUT of bloom and grain entirely; other fields stay.
      this.presentParams.bloomEnabled = false;
      this.presentParams.bloomIntensity = 0;
      this.presentParams.grain = 0;
    }
    // Composite config -- presets that don't supply one get the identity
    // (no mirror, contrast 1, brightness 0).
    const comp = preset.composite;
    this.compositeParams = {
      mode: comp?.mirror ?? MirrorMode.None,
      segments: comp?.kaleidoscopeSegments ?? 6,
      contrast: comp?.contrast ?? 1.0,
      brightness: comp?.brightness ?? 0,
    };
    this.presentPass.updateParams(this.presentParams);
    this.bloomPass.setParams(this.bloomParams);
  }

  // ---- Frame --------------------------------------------------------------

  step(nowMs: number): FrameInfo {
    const dtMs = this.lastClockMs > 0 ? Math.max(0.5, nowMs - this.lastClockMs) : 16.7;
    this.lastClockMs = nowMs;
    const fpsInst = 1000 / dtMs;
    this.smoothedFps = this.smoothedFps * 0.92 + fpsInst * 0.08;
    this.smoothedFrameMs = this.smoothedFrameMs * 0.92 + dtMs * 0.08;

    const time = nowMs / 1000;
    const audio = this.audio.read(time);
    const inj = this.beatInjector.consume(time);
    const beatTick = audio.beat.tick || inj.tick;
    const beatConfidence = Math.max(audio.beat.confidence, inj.confidence);
    const beatSource: 'real' | 'synth' | 'none' = inj.tick
      ? 'synth'
      : audio.beat.tick
        ? 'real'
        : 'none';
    // Override the AudioFrame's beat fields so downstream consumers (modulation
    // bindings on beatConfidence) see the merged value, while keeping the
    // detector's diagnostic fields (flux/threshold/bpm) intact for the HUD.
    audio.beat = {
      tick: beatTick,
      confidence: beatConfidence,
      flux: audio.beat.flux,
      threshold: audio.beat.threshold,
      bpm: audio.beat.bpm,
      msSinceLastBeat: audio.beat.msSinceLastBeat,
    };
    this.lastBeatSource = beatSource;

    // Drive the auto-cycler before reading the active selection so that any
    // switch this frame is reflected in the registry's `current` immediately.
    if (this.autoCycler?.isEnabled()) {
      const decision = this.autoCycler.step(nowMs, {
        rms1s: audio.history.rms1s,
        beatTick,
      });
      this.lastAutoStatus = decision.status;
      if (decision.switchTo !== null) {
        this.registry.loadById(decision.switchTo, nowMs);
      }
    } else {
      this.lastAutoStatus = {
        enabled: false,
        currentChildId: null,
        msUntilNextTarget: null,
        waitingForBeat: false,
        forceImminent: false,
      };
    }

    const sel = this.registry.active();

    // Refresh evaluator bindings only when the active presets change. This
    // avoids resetting the smoothing state every frame.
    if (this.currentPresetIdForEvaluator !== sel.current.id) {
      this.evaluatorA.setBindings(sel.current.modulation);
      this.currentPresetIdForEvaluator = sel.current.id;
    }
    const fromId = sel.transition?.from.id ?? null;
    if (this.currentTransitionFromIdForEvaluator !== fromId) {
      this.evaluatorB.setBindings(sel.transition?.from.modulation ?? []);
      this.currentTransitionFromIdForEvaluator = fromId;
    }

    // Reset working bundles to base preset values, then layer modulation.
    resetBundleFromPreset(this.bundleA, sel.current);
    this.evaluatorA.evaluate(audio, this.bundleA);

    let transitionT: number | null = null;
    if (sel.transition) {
      const t = clamp01(
        (nowMs - sel.transition.startMs) / Math.max(1, sel.transition.durationMs),
      );
      transitionT = t;
      resetBundleFromPreset(this.bundleB, sel.transition.from);
      this.evaluatorB.evaluate(audio, this.bundleB);
      lerpBundles(this.bundleB, this.bundleA, t, this.bundleOut);
    } else {
      copyBundle(this.bundleA, this.bundleOut);
    }

    // Warp-field lifecycle is preset-driven; reconcile when the active
    // preset id changes (and we use the destination of any in-flight
    // crossfade as the "owner" so the new field starts spinning up early).
    this.syncWarpFieldForActive(sel.current);
    this.syncPresentForPreset(sel.current);

    const encoder = this.device.createCommandEncoder({ label: 'frame' });

    // Drive the warp-field system. It may regenerate the staging slot or
    // start a new adoption; both happen as part of the same command buffer.
    let warpFieldT = 1;
    if (this.warpFieldSystem) {
      this.lastWarpFieldState = this.warpFieldSystem.step(
        encoder,
        nowMs,
        beatTick,
        beatConfidence,
      );
      warpFieldT = this.lastWarpFieldState.t;
    } else {
      this.lastWarpFieldState = null;
    }
    // The shader needs the live crossfade weight; it doesn't come from the
    // preset.
    this.bundleOut.warp.fieldT = warpFieldT;

    this.warpPass.updateParams(this.bundleOut.warp);
    this.seedPass.updateParams(this.bundleOut.seed);
    this.seedPass.uploadWaveform(audio.waveform);

    this.frameUniforms.write(this.device, {
      resolution: [this.surface.renderWidth, this.surface.renderHeight],
      aspect: this.surface.renderWidth / Math.max(1, this.surface.renderHeight),
      time,
      dt: dtMs / 1000,
      frameIndex: this.frameIndex,
      rms: audio.rms,
      bass: audio.bass,
      mid: audio.mid,
      treble: audio.treble,
      beatTick: beatTick ? 1 : 0,
      beatConfidence,
      bass1s: audio.history.bass1s,
      rms1s: audio.history.rms1s,
    });

    const fieldCurrView = this.warpFieldSystem
      ? this.warpFieldSystem.getCurrentView()
      : this.placeholderFieldView;
    const fieldPrevView = this.warpFieldSystem
      ? this.warpFieldSystem.getPreviousView()
      : this.placeholderFieldView;
    const warpBg = this.warpPass.makeBindGroup(
      this.ring.previousView,
      this.ring.linearSampler,
      fieldCurrView,
      fieldPrevView,
    );
    this.warpPass.encode(encoder, this.ring.currentView, warpBg);
    this.seedPass.encode(encoder, this.ring.currentView);
    // Composite: spatial transformation (mirror / kaleidoscope). Always
    // runs; mode = None is a near-zero-cost identity sample. The output
    // becomes the source for both bloom and present so they see the same
    // mirrored frame.
    this.compositePass.encode(encoder, this.ring.currentView, this.compositeParams);
    const compositeView = this.compositePass.getOutputView();
    // Bloom: only worth running if a preset opted in (bloomIntensity > 0).
    let bloomOutput = this.placeholderBloomView;
    if (this.presentParams.bloomEnabled && this.presentParams.bloomIntensity > 0) {
      this.bloomPass.encode(encoder, compositeView);
      bloomOutput = this.bloomPass.getOutputView();
    }
    const swapView = this.surface.context.getCurrentTexture().createView();
    const presentBg = this.presentPass.makeBindGroup(
      compositeView,
      this.ring.clampLinearSampler,
      bloomOutput,
    );
    this.presentPass.encode(encoder, swapView, presentBg);
    this.device.queue.submit([encoder.finish()]);
    rotateRing(this.ring);

    this.registry.tickTransition(nowMs);
    this.frameIndex++;

    return {
      fps: this.smoothedFps,
      frameMs: this.smoothedFrameMs,
      audio,
      presetId: sel.current.id,
      presetName: sel.current.name,
      transitionT,
      internalScale: this.surface.internalRenderScale,
      internalSize: [this.surface.renderWidth, this.surface.renderHeight],
      presentMode: this.presentParams.mode,
      beatInjectorMs: this.beatInjector.getIntervalMs(),
      beatSource: this.lastBeatSource,
      auto: this.lastAutoStatus,
      warpField: this.lastWarpFieldState,
    };
  }
}

// ---- helpers ----------------------------------------------------------------

function makeEmptyBundle(): ParamBundle {
  return {
    warp: {
      kind: 0,
      baseZoom: 0,
      bassZoomGain: 0,
      rotation: 0,
      bassRotationGain: 0,
      ripple: 0,
      rippleFreq: 0,
      decay: 1,
      chroma: 0,
      hueShift: 0,
      beatPunch: 0,
      centerDriftAmp: 0,
      centerDriftRate: 0,
      fieldT: 1,
      fieldStrength: 0,
      trailBoost: 0,
    },
    seed: {
      baseY: 0,
      gain: 0,
      halfThick: 0,
      intensity: 0,
      color: [0, 0, 0],
      edgeSharp: 0,
    },
  };
}

function resetBundleFromPreset(b: ParamBundle, preset: VisualizerPreset): void {
  b.warp.kind = preset.warp.kind;
  const w = preset.warp.params;
  b.warp.baseZoom = w.baseZoom;
  b.warp.bassZoomGain = w.bassZoomGain;
  b.warp.rotation = w.rotation;
  b.warp.bassRotationGain = w.bassRotationGain;
  b.warp.ripple = w.ripple;
  b.warp.rippleFreq = w.rippleFreq;
  b.warp.decay = w.decay;
  b.warp.chroma = w.chroma;
  b.warp.hueShift = w.hueShift;
  b.warp.beatPunch = w.beatPunch;
  b.warp.centerDriftAmp = w.centerDriftAmp;
  b.warp.centerDriftRate = w.centerDriftRate;
  // fieldT and fieldStrength are populated each frame from the
  // WarpFieldSystem's live state (or stay at safe defaults if no system).
  b.warp.fieldT = 1;
  b.warp.fieldStrength = preset.warpField?.strength ?? 0;
  b.warp.trailBoost = preset.warp.params.trailBoost ?? 0.015;
  const s = preset.seed.params;
  b.seed.baseY = s.baseY;
  b.seed.gain = s.gain;
  b.seed.halfThick = s.halfThick;
  b.seed.intensity = s.intensity;
  b.seed.color = [s.color[0], s.color[1], s.color[2]];
  b.seed.edgeSharp = s.edgeSharp;
}

function copyBundle(src: ParamBundle, dst: ParamBundle): void {
  Object.assign(dst.warp, src.warp);
  Object.assign(dst.seed, src.seed);
  dst.seed.color = [src.seed.color[0], src.seed.color[1], src.seed.color[2]];
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

function lerpBundles(
  from: ParamBundle,
  to: ParamBundle,
  t: number,
  out: ParamBundle,
): void {
  // Warp kind snaps to destination at the transition midpoint to avoid the
  // shader running an unrelated math path while the params interpolate.
  out.warp.kind = t < 0.5 ? from.warp.kind : to.warp.kind;
  out.warp.baseZoom = lerp(from.warp.baseZoom, to.warp.baseZoom, t);
  out.warp.bassZoomGain = lerp(from.warp.bassZoomGain, to.warp.bassZoomGain, t);
  out.warp.rotation = lerp(from.warp.rotation, to.warp.rotation, t);
  out.warp.bassRotationGain = lerp(from.warp.bassRotationGain, to.warp.bassRotationGain, t);
  out.warp.ripple = lerp(from.warp.ripple, to.warp.ripple, t);
  out.warp.rippleFreq = lerp(from.warp.rippleFreq, to.warp.rippleFreq, t);
  out.warp.decay = lerp(from.warp.decay, to.warp.decay, t);
  out.warp.chroma = lerp(from.warp.chroma, to.warp.chroma, t);
  out.warp.hueShift = lerp(from.warp.hueShift, to.warp.hueShift, t);
  out.warp.beatPunch = lerp(from.warp.beatPunch, to.warp.beatPunch, t);
  out.warp.centerDriftAmp = lerp(from.warp.centerDriftAmp, to.warp.centerDriftAmp, t);
  out.warp.centerDriftRate = lerp(from.warp.centerDriftRate, to.warp.centerDriftRate, t);
  out.warp.fieldT = lerp(from.warp.fieldT, to.warp.fieldT, t);
  out.warp.fieldStrength = lerp(from.warp.fieldStrength, to.warp.fieldStrength, t);
  out.warp.trailBoost = lerp(from.warp.trailBoost ?? 0, to.warp.trailBoost ?? 0, t);

  out.seed.baseY = lerp(from.seed.baseY, to.seed.baseY, t);
  out.seed.gain = lerp(from.seed.gain, to.seed.gain, t);
  out.seed.halfThick = lerp(from.seed.halfThick, to.seed.halfThick, t);
  out.seed.intensity = lerp(from.seed.intensity, to.seed.intensity, t);
  out.seed.color = [
    lerp(from.seed.color[0], to.seed.color[0], t),
    lerp(from.seed.color[1], to.seed.color[1], t),
    lerp(from.seed.color[2], to.seed.color[2], t),
  ];
  out.seed.edgeSharp = lerp(from.seed.edgeSharp, to.seed.edgeSharp, t);
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

// re-export so main.ts only needs to import from VisualizerEngine.
export type { SeedParams, WarpParams };
